import React, { FC, useEffect, useMemo } from 'react';
import './BillingForm.scss';
import { Controller, useForm } from 'react-hook-form';
import LabelComponent from 'app_components/commons/LabelComponent/LabelComponent';
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from 'react-icons/io';
import { CiWarning } from 'react-icons/ci';
import FormTextField from 'app_components/commons/FormTextField/FormTextField';
import FormSelect from 'app_components/commons/FormSelect/FormSelect';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup } from '@material-ui/core';
import { ERPClassification, ERPNature } from 'app_types/erp/erpTypes';
import ReactSelectCommon from 'app_components/commons/reactSelect';
import { ERP_COUNTRIES } from 'app_types/erp/erpCountries';
import { ERP_DEPARTMENTS } from 'app_types/erp/erpDepartments';
import { ERP_CITIES } from 'app_types/erp/erpCities';
import { EfipayCurrency } from 'app_types/efipay/efipayTypes';

export interface BillingFormData {
  fullName: string;
  firstName: string;
  lastName: string;
  docNumber: string;
  docType: string;
  country: any;
  department: any;
  city: any;
  nature: ERPNature;
  classification: ERPClassification;
  currency: EfipayCurrency;
  email: string;
}

interface BillingFormProps {
  onFormDataChange: (data: BillingFormData, isValid: boolean) => void;
  initialData?: Partial<BillingFormData>;
  totalPrice: number;
  userDocNumber?: string;
}

const BillingForm: FC<BillingFormProps> = ({
  onFormDataChange,
  initialData,
  userDocNumber
}) => {
  const formHook = useForm<BillingFormData>({
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      docNumber: initialData?.docNumber || userDocNumber || '',
      docType: initialData?.docType || '',
      country: initialData?.country || null,
      department: initialData?.department || null,
      city: initialData?.city || null,
      nature: initialData?.nature || ERPNature.NATURAL,
      classification: initialData?.classification || ERPClassification.NO_RESPONSABLE,
      currency: initialData?.currency || EfipayCurrency.COP,
      email: initialData?.email || ''
    },
    mode: 'onChange'
  });

  const { control, watch, setValue, formState: { errors, isValid } } = formHook;

  const currentNature = watch('nature');
  const country = watch('country');
  const department = watch('department');

  // Opciones para los selects
  const countriesToRender = useMemo(() => {
    return [...ERP_COUNTRIES];
  }, []);

  const departmentsToRender = useMemo(() => {
    return country?.label ? [...ERP_DEPARTMENTS?.filter(({ country: countryItem }) => country?.label === countryItem)] : [];
  }, [country]);

  const citiesToRender = useMemo(() => {
    return department?.value ? [...ERP_CITIES?.filter(({ department: departmentItem }) => departmentItem === department.value)] : [];
  }, [department]);

  // Actualizar clasificación cuando cambia la naturaleza
  useEffect(() => {
    if (currentNature === ERPNature.NATURAL) {
      setValue('classification', ERPClassification.NO_RESPONSABLE);
    } else {
      setValue('classification', ERPClassification.ENTIDADES_DEL_ESTADO);
    }
  }, [currentNature, setValue]);

  // Alternativa más segura
  // Alternativa usando campos específicos como dependencias
  useEffect(() => {
    // Actualizar el componente padre con los valores actuales
    onFormDataChange(formHook.getValues() as BillingFormData, isValid);
  }, [
    // Dependencias específicas para detectar cambios reales
    formHook.watch('firstName'),
    formHook.watch('lastName'),
    formHook.watch('docNumber'),
    formHook.watch('docType'),
    formHook.watch('country'),
    formHook.watch('department'),
    formHook.watch('city'),
    formHook.watch('nature'),
    formHook.watch('classification'),
    formHook.watch('currency'),
    formHook.watch('email'),
    isValid
  ]);

  return (
    <div className="billing-form">
      <div className="billing-form-personal-info">
        <div className="billing-form-warning">
          <CiWarning color="#C89000" size={16} />
          <LabelComponent color="#545C61" fontWeight={300} fontSize="75%">
            Esta información será utilizada para generar tu factura
          </LabelComponent>
        </div>

        <Controller
          control={control}
          rules={{ required: true }}
          name="currency"
          render={({ onChange, value }) => (
            <FormControl component="fieldset">
              <FormLabel component="legend" style={{ color: 'rgb(84, 92, 97)' }}>Moneda</FormLabel>
              <RadioGroup
                aria-label="currency"
                name="currency"
                value={value}
                onChange={onChange}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '5px'
                }}
              >
                {Object.keys(EfipayCurrency)
                  .filter((key) => EfipayCurrency[key as keyof typeof EfipayCurrency] === "COP")
                  .map((key) => (
                    <FormControlLabel
                      key={key}
                      value={EfipayCurrency[key as keyof typeof EfipayCurrency]}
                      control={<Radio
                        icon={<IoMdRadioButtonOff />}
                        checkedIcon={<IoMdRadioButtonOn color="var(--primary)" />}
                      />}
                      label={EfipayCurrency[key as keyof typeof EfipayCurrency]}
                      checked={value === EfipayCurrency[key as keyof typeof EfipayCurrency]}
                    />
                  ))}
              </RadioGroup>
            </FormControl>
          )}
        />

        <FormTextField
          control={control}
          id="firstName"
          error={errors.firstName}
          label="Nombre(s)"
          required
        />
        <FormTextField
          control={control}
          id="lastName"
          error={errors.lastName}
          label="Apellidos"
          required
        />

        <div className="billing-form-location">
          <div>
            <LabelComponent color="#545C61" fontWeight={300}>País</LabelComponent>
            <ReactSelectCommon
              isMulti={false}
              options={countriesToRender}
              formHooks={formHook}
              identifier="country"
              required
            />
            {formHook.formState.errors?.country && (
              <LabelComponent color="red">Este campo es obligatorio</LabelComponent>
            )}
          </div>

          <div>
            <LabelComponent color="#545C61" fontWeight={300}>Departamento</LabelComponent>
            <ReactSelectCommon
              isMulti={false}
              options={departmentsToRender}
              formHooks={formHook}
              identifier="department"
              required
            />
            {formHook.formState.errors?.department && (
              <LabelComponent color="red">Este campo es obligatorio</LabelComponent>
            )}
          </div>

          <div>
            <LabelComponent color="#545C61" fontWeight={300}>Ciudad</LabelComponent>
            <ReactSelectCommon
              isMulti={false}
              options={citiesToRender}
              formHooks={formHook}
              identifier="city"
              required
            />
            {formHook.formState.errors?.city && (
              <LabelComponent color="red">Este campo es obligatorio</LabelComponent>
            )}
          </div>
        </div>

        <div className="billing-form-document-info">
          <FormSelect
            control={control}
            id="nature"
            label="Naturaleza"
            variant="outlined"
            error={!!errors?.nature}
            required
          >
            <MenuItem value={ERPNature.NATURAL}>Persona Natural</MenuItem>
            <MenuItem value={ERPNature.JURIDICA}>Persona Jurídica</MenuItem>
          </FormSelect>

          {currentNature === ERPNature.JURIDICA && (
            <FormSelect
              control={control}
              id="classification"
              label="Clasificación"
              variant="outlined"
              error={!!errors?.classification}
              required
            >
              {Object.values(ERPClassification)
                .filter((value) => value !== ERPClassification.NO_RESPONSABLE)
                .map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))
              }
            </FormSelect>
          )}

          <FormTextField
            control={control}
            id="docType"
            error={errors.docType}
            label="Tipo de documento"
            required
          />

          <FormTextField
            control={control}
            id="docNumber"
            error={errors.docNumber}
            label="Número de documento"
            required
          />

          <FormTextField
            control={control}
            id="email"
            error={errors.email}
            label="Correo electrónico"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default BillingForm;